import {
  Facebook,
  Globe,
  Instagram,
  Mail,
  MessageCircle,
  MessageSquare,
  Phone,
  RefreshCw,
  Send,
  Settings2,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import type { Inbox } from '../../api/types';
import { Badge } from '../../components/Badge';
import {
  channelHealth,
  identityLine,
  providerLine,
  reconnectViaOauth,
} from '../../lib/channelMeta';
import { launchReconnect } from '../../oauth/launch';

const TYPE_ICON = {
  email: Mail,
  ringcentral: Phone,
  telegram: Send,
  twilio: MessageSquare,
  website: Globe,
  whatsapp: MessageCircle,
  facebook: Facebook,
  instagram: Instagram,
} as const;

export function ChannelCard({ inbox, onError }: { inbox: Inbox; onError: (message: string) => void }) {
  const [leaving, setLeaving] = useState(false);
  const health = channelHealth(inbox);
  const Icon = TYPE_ICON[inbox.channelType];

  const reconnect = async () => {
    setLeaving(true);
    try {
      await launchReconnect(inbox);
      // Navigation away is the success path — no state to reset.
    } catch (e) {
      setLeaving(false);
      onError(e instanceof Error ? e.message : 'Could not start the sign-in.');
    }
  };

  return (
    <div className="card channel-card">
      <div className="channel-icon">
        <Icon size={17} />
      </div>
      <div className="channel-lines">
        <div className="channel-identity">{identityLine(inbox)}</div>
        <div className="channel-provider">
          {providerLine(inbox)}
          {inbox.channelType === 'whatsapp' && (inbox.channel.templateCount ?? 0) > 0 && (
            <> · {inbox.channel.templateCount} templates</>
          )}
        </div>
        <div className="channel-inbox-name">Inbox: {inbox.name}</div>
      </div>
      <div className="channel-side">
        {health === 'connected' ? (
          <Badge tone="ok" dot>
            Connected
          </Badge>
        ) : (
          <Badge tone="warn" dot>
            Needs attention
          </Badge>
        )}
        {/* The one number automated SMS goes out from (services/smsDefault.js). */}
        {inbox.defaultSmsSender && <Badge tone="accent">Default SMS</Badge>}
        <div className="channel-actions">
          {health === 'needs_attention' && reconnectViaOauth(inbox) && (
            <button className="btn btn-primary" onClick={() => void reconnect()} disabled={leaving}>
              <RefreshCw size={13} />
              Reconnect
            </button>
          )}
          <Link to={`/inboxes/${inbox.id}`} className="btn btn-secondary">
            <Settings2 size={13} />
            Manage
          </Link>
        </div>
      </div>
    </div>
  );
}
